import apiClient from "./apiClient";
import { ENDPOINTS } from "./config";

export interface WishlistCourse {
  id: string;
  userId: string;
  courseId: string;
  createdAt: string;
  course: {
    id: string;
    title: string;
    shortName?: string;
    description?: string;
    level: string;
    duration: number;
    studyMode: string;
    language?: string;
    tuitionFee?: number;
    applicationFee?: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    university: {
      id: string;
      name: string;
      shortName?: string;
      city: string;
      country: string;
      logoUrl?: string;
      isActive: boolean;
    };
  };
}

export interface WishlistResponse {
  wishlistItems: WishlistCourse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

class WishlistService {
  /**
   * Get user's wishlist
   */
  async getUserWishlist(params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<WishlistResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());

    const url = `${ENDPOINTS.USERS.WISHLIST}${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await apiClient.get<ApiResponse<WishlistResponse>>(
      url,
      undefined,
      true // withAuth = true
    );

    if (!response.data) {
      throw new Error("No data received from server");
    }

    return response.data;
  }

  /**
   * Add course to wishlist
   */
  async addToWishlist(courseId: string): Promise<ApiResponse<WishlistCourse>> {
    const response = await apiClient.post<ApiResponse<WishlistCourse>>(
      ENDPOINTS.USERS.ADD_TO_WISHLIST(courseId),
      {},
      true // withAuth = true
    );

    if (!response.data) {
      throw new Error("No data received from server");
    }

    return response.data;
  }

  /**
   * Remove course from wishlist
   */
  async removeFromWishlist(
    courseId: string
  ): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      ENDPOINTS.USERS.REMOVE_FROM_WISHLIST(courseId),
      true // withAuth = true
    );

    if (!response.data) {
      throw new Error("No data received from server");
    }

    return response.data;
  }

  /**
   * Check if course is in user's wishlist
   */
  async isInWishlist(courseId: string): Promise<boolean> {
    try {
      const wishlistResponse = await this.getUserWishlist({
        page: 1,
        limit: 100,
      });
      const wishlistItems = wishlistResponse.data.wishlistItems;

      return wishlistItems.some((item) => item.courseId === courseId);
    } catch (error) {
      console.error("Error checking wishlist status:", error);
      return false;
    }
  }
}

const wishlistService = new WishlistService();
export default wishlistService;
